import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { clamp, damp, deadzone } from '../engine/mathUtils';
import type { InputHub } from '../input/InputHub';
import { useRafLoop } from './useRafLoop';

const MAX_ANGLE = Math.PI; // 180 Grad Anschlag in jede Richtung
const DEADZONE = 0.05;

interface SteeringWheelProps {
  hub: InputHub;
  vibrate: boolean;
}

/**
 * Echtes Lenkrad als SVG. Der Winkel zwischen Fingerposition und
 * Lenkradmitte steuert den Einschlag; beim Loslassen dreht es weich zurueck.
 *
 * Bewusst 2D: kostet nichts vom 3D-Budget und bleibt bei jeder
 * Aufloesungsskalierung gestochen scharf.
 */
export function SteeringWheel({ hub, vibrate }: SteeringWheelProps): JSX.Element {
  const zoneRef = useRef<HTMLDivElement>(null);
  const rotorRef = useRef<SVGGElement>(null);
  const angle = useRef(0);
  const dragging = useRef(false);
  const lastPointerAngle = useRef(0);
  const pointerId = useRef<number | null>(null);

  const pointerAngle = useCallback((e: ReactPointerEvent<HTMLDivElement>): number => {
    const el = zoneRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2));
  }, []);

  const onDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (pointerId.current !== null) return;
      pointerId.current = e.pointerId;
      dragging.current = true;
      lastPointerAngle.current = pointerAngle(e);
      zoneRef.current?.setPointerCapture(e.pointerId);
      if (vibrate && 'vibrate' in navigator) navigator.vibrate(6);
    },
    [pointerAngle, vibrate],
  );

  const onMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragging.current || e.pointerId !== pointerId.current) return;
      e.preventDefault();
      const a = pointerAngle(e);
      // Differenz auf (-PI, PI] normalisieren, damit der Sprung bei +-180 Grad
      // nicht zu einem Vollausschlag fuehrt.
      let d = a - lastPointerAngle.current;
      if (d > Math.PI) d -= Math.PI * 2;
      if (d < -Math.PI) d += Math.PI * 2;
      lastPointerAngle.current = a;
      angle.current = clamp(angle.current + d, -MAX_ANGLE, MAX_ANGLE);
    },
    [pointerAngle],
  );

  const onUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== pointerId.current) return;
    dragging.current = false;
    pointerId.current = null;
    if (zoneRef.current?.hasPointerCapture(e.pointerId)) {
      zoneRef.current.releasePointerCapture(e.pointerId);
    }
  }, []);

  useRafLoop((dt) => {
    if (!dragging.current) {
      angle.current = damp(angle.current, 0, 9, dt);
      if (Math.abs(angle.current) < 0.001) angle.current = 0;
    }
    hub.touch.steer = deadzone(angle.current / MAX_ANGLE, DEADZONE);
    const rotor = rotorRef.current;
    if (rotor) {
      rotor.style.transform = `rotate(${(angle.current * 180) / Math.PI}deg)`;
    }
  });

  return (
    <div
      ref={zoneRef}
      className="wheel-zone"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onContextMenu={(e) => e.preventDefault()}
      role="slider"
      aria-label="Lenkrad"
      aria-valuemin={-100}
      aria-valuemax={100}
      aria-valuenow={0}
    >
      <svg viewBox="0 0 200 200" aria-hidden="true">
        <g ref={rotorRef} className="wheel-rotor">
          <circle cx="100" cy="100" r="88" fill="none" stroke="#0d141d" strokeWidth="26" />
          <circle cx="100" cy="100" r="88" fill="none" stroke="#2b3746" strokeWidth="18" />
          <circle
            cx="100"
            cy="100"
            r="96"
            fill="none"
            stroke="rgba(255,255,255,0.16)"
            strokeWidth="3"
          />
          <rect x="14" y="92" width="172" height="17" rx="8" fill="#243040" />
          <rect x="92" y="100" width="17" height="80" rx="8" fill="#243040" />
          <circle cx="100" cy="100" r="30" fill="#182430" />
          <circle cx="100" cy="100" r="30" fill="none" stroke="#33445a" strokeWidth="4" />
          <path d="M100 84 l12 20 h-24 z" fill="#ffc21a" />
        </g>
      </svg>
    </div>
  );
}
